<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import questionBackground from '../assets/design/backgrounds/question-background.png'
import QuestionRenderer from '../components/QuestionRenderer.vue'
import { useExperienceStore } from '../stores/experience'
import type { Question } from '../types/questionnaire'

const router = useRouter()
const experienceStore = useExperienceStore()

const stepClass = computed(() => `experience-view--step-${experienceStore.currentStepIndex + 1}`)

const canContinue = computed(() => {
  const step = experienceStore.currentStep
  if (!step) {
    return false
  }

  return step.questions.every((question: Question) => {
    if (!question.required) {
      return true
    }

    const answer = experienceStore.answers[question.code]?.trim()
    if (!answer) {
      return false
    }

    return answer !== 'custom'
      || Boolean(experienceStore.customAnswers[question.code]?.trim())
  })
})

onMounted(async () => {
  await experienceStore.ensureQuestionnaire()

  if (!experienceStore.questionnaire) {
    await router.replace({ name: 'home' })
  }
})

function next() {
  experienceStore.nextStep()
}

async function submit() {
  if (await experienceStore.submitCurrentSession()) {
    await router.replace({ name: 'map' })
  }
}

function goHome() {
  experienceStore.resetSession()
  void router.replace({ name: 'home' })
}
</script>

<template>
  <div
    v-if="experienceStore.currentStep"
    class="view experience-view"
    :class="stepClass"
    :style="{ backgroundImage: `url(${questionBackground})` }"
  >
    <header class="question-page-heading">
      <p class="step-eyebrow">{{ experienceStore.currentStep.eyebrow }}</p>
      <h1 class="step-title">{{ experienceStore.currentStep.title }}</h1>
      <p class="step-description">{{ experienceStore.currentStep.description }}</p>
    </header>

    <div class="questions">
      <QuestionRenderer
        v-for="question in experienceStore.currentStep.questions"
        :key="question.code"
        :question="question"
        :model-value="experienceStore.answers[question.code] || ''"
        :custom-value="experienceStore.customAnswers[question.code] || ''"
        @update:model-value="experienceStore.setAnswer(question.code, $event)"
        @update:custom-value="experienceStore.setCustomAnswer(question.code, $event)"
      />
    </div>

    <p v-if="experienceStore.errorMessage" class="step-error" role="alert">
      {{ experienceStore.errorMessage }}
    </p>

    <footer class="step-actions">
      <button
        class="step-secondary-button"
        type="button"
        @click="experienceStore.currentStepIndex > 0 ? experienceStore.previousStep() : goHome()"
      >
        {{ experienceStore.currentStepIndex > 0 ? '上一步' : '返回首页' }}
      </button>
      <button
        v-if="!experienceStore.isLastStep"
        class="step-primary-button"
        type="button"
        :class="{ 'step-primary-button--ready': canContinue }"
        @click="next"
      >
        下一步
      </button>
      <button
        v-else
        class="step-primary-button"
        :class="{ 'step-primary-button--ready': canContinue }"
        type="button"
        :disabled="experienceStore.submitting"
        @click="submit"
      >
        {{ experienceStore.submitting ? '正在提交…' : '提交' }}
      </button>
    </footer>
  </div>
</template>

